import { useQuery } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getCandidatesAddedByCampaignDemoData } from "../../Reports/CandidatesAddedByCampaign/DemoData/CandidatesAddedByCampaignDemoData";
import { useAppSelector } from "../State/Hooks/reduxHooks";
import { selectClientValue } from "../State/Slices/SelectedClientSlice";
import { IFeatureToggles } from "../Typings/FeatureToggles";

import dayjs from "dayjs";
import { toUtcEst } from "../../MessagingCenter/Utils";
import { MessageDeliveryType } from "../Enums/Message";
import { useAuthToken } from "../State/Hooks/authHooks";
import { useUserProfileStore } from "../State/Stores/UserProfileStore";
import { IMessagePerformanceDto, IPagination } from "../Typings/MessageText";
import { IPerformanceReport } from "../Typings/Reports/CampaignEmailPerformanceReport";
import {
  ICandidatesAddedByCampaignReport,
  ICandidatesAddedByCampaignTableData,
} from "../Typings/Reports/CandidatesAddedByCampaign";
import { ICandidatesAddedDataPoint, ICandidatesAddedReport } from "../Typings/Reports/CandidatesAddedReport";
import { createPaginationQueryString, secureFetcher } from "./queryHelpers";

export const usePerformanceReportQuery = (
  startDate: Date,
  endDate: Date,
  deliveryMethod: MessageDeliveryType,
  groupId?: string,
  enabled = true,
) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  const { useDemoData = false } = useFlags<IFeatureToggles>();

  return useQuery<IPerformanceReport>(
    ["performanceReport", { clientId, startDate, endDate, groupId, deliveryMethod, enabled }],
    async () => {
      let data = {} as IPerformanceReport;
      if (useDemoData) {
        data = getPerformanceDemoData(startDate, endDate);
      } else {
        const queryParams = new URLSearchParams({
          startDate: formatStartDate(startDate),
          endDate: formatEndDate(endDate),
          deliveryMethod: deliveryMethod.toString(),
          groupId: groupId ?? "",
        });
        data = (await secureFetcher(
          `/reporting/${clientId}/messaging/performance?${queryParams}`,
          token,
        )) as IPerformanceReport;
      }

      for (const dataPoint of data.dataPoints) {
        dataPoint.dateAdded = dayjs(dataPoint.dateAdded, "YYYY-MM-DD").format("MM/DD/YYYY");
      }

      return data;
    },
    {
      enabled: !!clientId && enabled && !!token,
    },
  );
};

/**
 * hook to fetch data for the Candidates Added Report
 * @param startDate
 * @param endDate
 * @returns
 */
export const useCandidatesAddedReportQuery = (startDate: Date, endDate: Date) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  const { useDemoData = false } = useFlags<IFeatureToggles>();

  return useQuery<ICandidatesAddedDataPoint[]>(
    ["candidatesAddedReport", clientId, startDate, endDate, useDemoData],
    async () => {
      if (useDemoData) {
        return getCandidatesAddedReportDemoData(startDate, endDate);
      }

      const queryParams = new URLSearchParams({
        startDate: formatStartDate(startDate),
        endDate: formatEndDate(endDate),
      });
      const response = (await secureFetcher(
        `/reporting/${clientId}/candidates/added?${queryParams}`,
        token,
      )) as ICandidatesAddedReport;

      const candidatesAdded = response.candidatesAdded;

      const dataPoints = candidatesAdded.map((candidateCount) => ({
        dateAdded: dayjs(candidateCount.dateAdded, "YYYY-MM-DD").format("MM/DD/YYYY"),
        candidateCount: candidateCount.candidateCount,
      })) as ICandidatesAddedDataPoint[];

      return dataPoints;
    },
    {
      enabled: !!clientId && !!token,
    },
  );
};

/**
 * hook to fetch data for the Candidates Added By Campaign report
 * @param startDate
 * @param endDate
 * @returns
 */
export const useCandidatesAddedByCampaignReportQuery = (
  startDate: Date,
  endDate: Date,
  campaignId?: string,
  enabled = true,
) => {
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);
  const { useDemoData = false } = useFlags<IFeatureToggles>();

  return useQuery<ICandidatesAddedByCampaignReport | null>(
    ["candidatesAddedByCampaignReport", clientId, startDate, endDate, campaignId, enabled, useDemoData],
    async () => {
      let report: ICandidatesAddedByCampaignReport | null = null;
      if (useDemoData) {
        report = getCandidatesAddedByCampaignDemoData(startDate, endDate, campaignId);
      } else {
        const queryParams = new URLSearchParams({
          startDate: formatStartDate(startDate),
          endDate: formatEndDate(endDate),
        });
        if (campaignId) {
          queryParams.append("campaignId", campaignId);
        }

        const response = (await secureFetcher(
          `/reporting/${clientId}/campaign/candidates/added?${queryParams}`,
          token,
        )) as ICandidatesAddedByCampaignReport;

        const tableData = response.tableData.map((dataPoint) => {
          const point = {} as ICandidatesAddedByCampaignTableData;
          for (const key in dataPoint) {
            if (key === "dateAdded") {
              point[key] = toUtcEst(dayjs(dataPoint.dateAdded.toString())).format("MM/DD/YYYY");
            } else {
              point[key] = Number(dataPoint[key]);
            }
          }
          return point;
        });

        report = {
          campaignsInReport: response.campaignsInReport,
          tableData,
        } as ICandidatesAddedByCampaignReport;
      }

      const maxCampaignsToShow = 5;
      return report && report.campaignsInReport.length > maxCampaignsToShow
        ? getTopProducingCampaigns(report, maxCampaignsToShow)
        : report;
    },
    {
      enabled: !!clientId && enabled && !!token,
    },
  );
};

export const useGetMessagePerformanceIndividual = (
  deliveryMethod: MessageDeliveryType,
  page: number,
  pageSize: number,
  search?: string,
  sortBy?: string,
  sortDirection?: "asc" | "desc",
  groupId?: string,
  startDate?: Date,
  endDate?: Date,
  enabled = true,
) => {
  let queryString = createPaginationQueryString({
    page,
    pageSize,
    search,
    sortBy,
    sortDirection,
  });
  const token = useAuthToken();
  const { clientId } = useAppSelector(selectClientValue);

  const { fromUserAdjustedDate } = useUserProfileStore();

  if (groupId) {
    queryString += `&groupId=${groupId}`;
  }
  // `.utc(true)` keeps the picker's wall-clock components; `fromUserAdjustedDate` re-anchors them to the user's profile TZ before serialising.
  if (startDate && dayjs(startDate).isValid()) {
    queryString += `&startDate=${fromUserAdjustedDate(dayjs(startDate).utc(true)).toISOString()}`;
  }
  if (endDate && dayjs(endDate).isValid()) {
    queryString += `&endDate=${fromUserAdjustedDate(dayjs(endDate).utc(true)).toISOString()}`;
  }

  queryString += `&deliveryMethod=${deliveryMethod}`;

  return useQuery<IPagination<IMessagePerformanceDto>>(
    ["messagePerformanceIndividual", clientId, queryString, enabled],
    () => secureFetcher(`/reporting/${clientId}/messaging/performance/individual${queryString}`, token),
    {
      enabled: !!clientId && enabled && !!token,
    },
  );
};

const formatStartDate = (date: Date) => {
  return date.toISOString().substring(0, date.toISOString().indexOf("T")) + "T00:00:00.000Z";
};

const formatEndDate = (date: Date) => {
  return date.toISOString().substring(0, date.toISOString().indexOf("T")) + "T23:59:59.999Z";
};

const getTopProducingCampaigns = (
  report: ICandidatesAddedByCampaignReport | null,
  numberCampaignsToKeep: number,
) => {
  if (!report) {
    return null;
  }

  let campaignsInReportCopy = report.campaignsInReport;

  //sort by highest campaignCount
  campaignsInReportCopy.sort((a, b) => b.countInReportPeriod - a.countInReportPeriod);
  //take top # campaigns
  campaignsInReportCopy = campaignsInReportCopy.slice(0, numberCampaignsToKeep);

  /**
   * We're leaving all the data in report.tableData for now. The bars are generated by the
   * contents of report.campaignsInReport, so if we truncate this, we won't use the other data
   */
  report.campaignsInReport = campaignsInReportCopy;

  return report;
};

const getPerformanceDemoData = (startDate: Date, endDate: Date) => {
  const data = {
    dataPoints: [],
  } as IPerformanceReport;
  const date = new Date(endDate);

  while (date >= startDate) {
    date.setDate(date.getDate() - 1);

    data.dataPoints.push({
      dateAdded: date.toISOString(),
      deliveredCount: getRandomInt(75),
      bounceCount: getRandomInt(25),
      clickCount: getRandomInt(75),
      openCount: getRandomInt(75),
      replyCount: getRandomInt(75),
      unsubscribeCount: getRandomInt(20),
    });
  }

  data.dataPoints = data.dataPoints.reverse();

  return data;
};

const getCandidatesAddedReportDemoData = (startDate: Date, endDate: Date) => {
  const demoData: ICandidatesAddedDataPoint[] = [];
  const date = new Date(endDate);

  while (date >= startDate) {
    date.setDate(date.getDate() - 1);
    demoData.push({
      dateAdded: date.toLocaleDateString(),
      candidateCount: getRandomInt(180),
    });
  }

  demoData.reverse();

  return demoData;
};

function getRandomInt(max: number) {
  return Math.floor(Math.random() * max);
}
